/**
 * 通用 store：单一状态对象 + 选择器订阅 + 帧级批量通知。
 *
 * 语义约定（下游所有 store 用法都依赖这三条）：
 * 1. `set()` 后 `get()` **立即**返回新状态——乐观更新的同步读取不等帧；
 *    只有「通知订阅者」被合并到下一帧执行一次。
 * 2. 订阅带选择器：通知帧里对每个订阅重算 `selector(state)`，与上次值
 *    `equals`（默认 Object.is）相等则跳过——组件只因自己用到的字段重渲染。
 * 3. 选择器返回新建对象时必须配 `shallowEqual`，否则每帧都通知。
 *
 * 状态对象按不可变约定更新（set 浅合并 / updater 返回新对象），
 * 选择器可以安全地把子对象引用当作「变没变」的依据。
 */
import type { Scheduler } from '~/util/schedule';

export type Equals<T> = (a: T, b: T) => boolean;

export interface Store<S extends object> {
  get(): S;
  /** 浅合并补丁或函数式更新。同一帧内多次 set 只触发一次通知。 */
  set(patch: Partial<S> | ((prev: S) => S)): void;
  /**
   * 订阅 selector 选中值的变化。返回退订函数。
   * 订阅时不立即回调（初值用 get() 自取）；onChange 收到的是新选中值。
   */
  subscribe<T>(selector: (state: S) => T, onChange: (value: T) => void, equals?: Equals<T>): () => void;
  /** 立即执行一次通知（测试、以及必须同步落地的关键路径用；无待通知变更时是 no-op）。 */
  flushSync(): void;
}

interface Subscription<S> {
  selector: (state: S) => unknown;
  equals: Equals<unknown>;
  onChange: (value: unknown) => void;
  last: unknown;
  active: boolean;
}

export function createStore<S extends object>(initial: S, scheduler: Scheduler): Store<S> {
  let state = initial;
  let dirty = false;
  const subscriptions = new Set<Subscription<S>>();

  function flush(): void {
    if (!dirty) return; // flushSync 已先执行过，或 set 的排队被它清空
    dirty = false;
    // 快照：flush 期间新增的订阅不参与本轮（其初值取的已是当前状态）
    const batch = [...subscriptions];
    for (const sub of batch) {
      if (!sub.active) continue; // flush 期间被退订
      const next = sub.selector(state);
      if (sub.equals(sub.last, next)) continue;
      sub.last = next;
      // onChange 里再 set 会把 dirty 置回 true 并排队下一帧，不在本轮递归
      sub.onChange(next);
    }
  }

  return {
    get: () => state,
    set(patch) {
      state = typeof patch === 'function' ? patch(state) : { ...state, ...patch };
      if (!dirty) {
        dirty = true;
        scheduler(flush);
      }
    },
    subscribe(selector, onChange, equals = Object.is) {
      const sub: Subscription<S> = {
        selector,
        equals: equals as Equals<unknown>,
        onChange: onChange as (value: unknown) => void,
        last: selector(state),
        active: true,
      };
      subscriptions.add(sub);
      return () => {
        sub.active = false;
        subscriptions.delete(sub);
      };
    },
    flushSync: flush,
  };
}

/**
 * 一层浅比较：选择器返回「现拼的对象/数组」时作 equals 用。
 * 仅比较自有可枚举键的 Object.is，不递归。
 */
export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  const recordA = a as Record<string, unknown>;
  const recordB = b as Record<string, unknown>;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(recordB, key) || !Object.is(recordA[key], recordB[key])) {
      return false;
    }
  }
  return true;
}
